import { LIMITS, toggleTodo } from '@wiztime/core';
import { queries } from '@wiztime/storage';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLive } from '../../../hooks/useLive';
import { useRunCommand } from '../../../hooks/useRunCommand';
import { useStore } from '../../../hooks/useServices';
import { Button, ButtonVariant } from '../../../ui/Button';
import { Card } from '../../../ui/Card';
import { loadedNoteEditor, loadNoteEditor, type NoteEditorModule } from './loadNoteEditor';
import { loadedNoteMarkdown, loadNoteMarkdown, type NoteMarkdownModule } from './loadNoteMarkdown';
import type { NoteEditorHandle } from './noteEditorTypes';
import { NoteTextarea } from './NoteTextarea';
import { SOURCE_LINE_ATTRIBUTE } from './noteLinks';
import styles from './NoteCard.module.css';

/** A note session ends after this long without typing (requirements §3.9). */
export const NOTE_IDLE_MS = 2_000;

/** State of the save the editing row reports (design 2026-10-15 §2.3). */
const SaveState = { Idle: 'idle', Saving: 'saving', Failed: 'failed' } as const;
type SaveState = (typeof SaveState)[keyof typeof SaveState];

interface NoteEdit {
  readonly text: string;
  /** Typed but not yet saved. */
  readonly dirty: boolean;
}

/** Offset of the end of the zero-based `lineIndex` in `text`; the end of the text when there is no such line. */
function endOfLine(text: string, lineIndex: number): number {
  let offset = 0;
  const lines = text.split('\n');
  for (let index = 0; index <= lineIndex && index < lines.length; index += 1) {
    offset += (lines[index] ?? '').length + (index > 0 ? 1 : 0);
  }
  return lineIndex < lines.length ? offset : text.length;
}

/**
 * The day's note, in two states (design 2026-10-15 §4, 2026-10-15b §2): the editing state is a
 * Markdown-styled editor (CodeMirror, loaded on demand; a plain text box until then) that grows with
 * its content (no inner scrollbar), with a row below it for the save status and the buttons 取消
 * and 保存; the reading state shows the note as Markdown and takes over when the editor loses focus.
 * A click on the rendered note (or Enter on it) starts editing with the caret on the clicked line;
 * ticking a to-do in the rendered note saves at once without editing. An empty note always shows
 * the editor. Typing is saved on blur, on 保存, on Ctrl/⌘+Enter, on Esc or after two idle seconds:
 * one undo step per session. 取消 puts the text from the start of editing back (one undo step).
 */
export function NoteCard({ date }: { date: string }) {
  const { t } = useTranslation();
  const store = useStore();
  const run = useRunCommand();
  const note = useLive(() => queries.noteForDate(store.repo, date), [store, date]);
  const stored = note.data?.data?.text ?? '';

  const [edit, setEdit] = useState<NoteEdit | null>(null);
  const editRef = useRef<NoteEdit | null>(null);
  const timer = useRef<number | null>(null);
  const [editing, setEditing] = useState(false);
  const editingRef = useRef(false);
  /** The text when editing started: what 取消 puts back. */
  const startText = useRef('');
  const [saveState, setSaveState] = useState<SaveState>(SaveState.Idle);
  const inflight = useRef<Promise<unknown> | null>(null);
  /** Where the caret goes when editing starts from the rendered note; null means the end of the text. */
  const caretOffset = useRef<number | null>(null);
  const lastCaret = useRef<number | null>(null);
  const handle = useRef<NoteEditorHandle>(null);
  const region = useRef<HTMLDivElement>(null);

  // Once the saved text equals what was typed, follow the stored value again (remote edits show up).
  if (edit !== null && !edit.dirty && edit.text === stored) setEdit(null);
  const value = edit?.text ?? stored;
  const showField = editing || value === '';

  const update = useCallback((next: NoteEdit | null) => {
    editRef.current = next;
    setEdit(next);
  }, []);

  const clearTimer = useCallback(() => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  }, []);

  // The flush on unmount resolves after the card is gone; its outcome then updates no state.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /** Write the typed text; resolves to false when the save failed (the text stays pending). */
  const flush = useCallback(async (): Promise<boolean> => {
    clearTimer();
    const current = editRef.current;
    if (current === null || !current.dirty) return true;
    update({ text: current.text, dirty: false });
    setSaveState(SaveState.Saving);
    const saving = run(() => store.saveNote(date, current.text));
    inflight.current = saving;
    const result = await saving;
    if (inflight.current === saving) inflight.current = null;
    if (!mounted.current) return result.ok;
    // A failed save keeps the text pending, unless the user has typed or ticked something newer meanwhile.
    if (!result.ok && editRef.current?.text === current.text) update({ text: current.text, dirty: true });
    setSaveState(result.ok ? SaveState.Idle : SaveState.Failed);
    return result.ok;
  }, [clearTimer, update, run, store, date]);

  const change = (text: string): void => {
    update({ text, dirty: true });
    clearTimer();
    timer.current = window.setTimeout(() => void flush(), NOTE_IDLE_MS);
  };

  // A stable callback (the Markdown tick boxes must not remount when the note changes, or the
  // keyboard focus on a box is lost after Space): it reads the latest text and `flush` from refs.
  const latest = useRef({ text: value, flush });
  useLayoutEffect(() => {
    latest.current = { text: value, flush };
  });
  const toggle = useCallback(
    (lineIndex: number): void => {
      const next = toggleTodo(latest.current.text, lineIndex);
      editRef.current = { text: next, dirty: true };
      setEdit({ text: next, dirty: true });
      void latest.current.flush();
    },
    [],
  );

  // Save pending text when the date changes (the card remounts per date), the tab hides or the page unloads.
  useEffect(() => {
    const onHide = (): void => {
      if (document.visibilityState === 'hidden') void flush();
    };
    document.addEventListener('visibilitychange', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      void flush();
    };
  }, [flush]);

  // The Markdown renderer loads on demand; until then (or if it fails to load) the note reads as plain text.
  const [markdown, setMarkdown] = useState<NoteMarkdownModule | null>(loadedNoteMarkdown);
  const wantsMarkdown = value !== '' && markdown === null;
  useEffect(() => {
    if (!wantsMarkdown) return;
    let alive = true;
    loadNoteMarkdown().then(
      (module) => {
        if (alive) setMarkdown(module);
      },
      (error: unknown) => {
        console.warn('note markdown renderer failed to load; showing plain text', { error });
      },
    );
    return () => {
      alive = false;
    };
  }, [wantsMarkdown]);

  // The CodeMirror editor loads on demand too; until then (or if it fails to load) a plain text box edits the note.
  const [editorModule, setEditorModule] = useState<NoteEditorModule | null>(loadedNoteEditor);
  useEffect(() => {
    if (editorModule !== null) return;
    let alive = true;
    loadNoteEditor().then(
      (module) => {
        if (alive) setEditorModule(module);
      },
      (error: unknown) => {
        console.warn('note editor failed to load; editing in a plain text box', { error });
      },
    );
    return () => {
      alive = false;
    };
  }, [editorModule]);
  const Editor = editorModule?.default ?? NoteTextarea;

  // The editor component was swapped while the user was in it (the chunk arrived mid-typing): carry the caret over.
  // Removing the focused text box blurs it: that blur is part of the swap, not the user leaving
  // (`onRegionBlur` ignores it). The flag is raised in render because the removal happens during
  // the commit, before any effect runs.
  const previousEditor = useRef(Editor);
  const swapping = useRef(false);
  // eslint-disable-next-line react-hooks/refs -- must be set before the commit removes the old editor
  if (previousEditor.current !== Editor) swapping.current = true;
  useLayoutEffect(() => {
    if (previousEditor.current === Editor) return;
    previousEditor.current = Editor;
    swapping.current = false;
    if (editingRef.current) handle.current?.focus(lastCaret.current);
  }, [Editor]);

  const setEditingState = useCallback((next: boolean) => {
    if (next && !editingRef.current) startText.current = latest.current.text;
    editingRef.current = next;
    setEditing(next);
  }, []);

  // Entering the editing state from the rendered note: focus the editor and put the caret on the clicked line.
  useLayoutEffect(() => {
    if (!editing || handle.current === null) return;
    const caret = caretOffset.current;
    caretOffset.current = null;
    if (region.current?.contains(document.activeElement) === true && caret === null) return;
    handle.current.focus(caret);
  }, [editing]);

  const startEditing = (lineIndex: number | null): void => {
    caretOffset.current = lineIndex === null ? null : endOfLine(value, lineIndex);
    setEditingState(true);
  };

  /** Leave editing: the rendered note takes over (an empty note keeps its editor, unfocused). */
  const leave = useCallback((): void => {
    setEditingState(false);
    handle.current?.blur();
  }, [setEditingState]);

  /** 保存, Ctrl/⌘+Enter: save now and return to reading; a failed save stays in editing. */
  const save = useCallback(async (): Promise<void> => {
    if (await flush()) leave();
  }, [flush, leave]);

  /** Esc: like losing focus, the typed text is saved and never discarded. */
  const escape = useCallback((): void => {
    void flush();
    leave();
  }, [flush, leave]);

  /** 取消: the text from the start of editing comes back; if the note changed meanwhile that is one undo step. */
  const cancel = useCallback(async (): Promise<void> => {
    clearTimer();
    const original = startText.current;
    update({ text: original, dirty: false });
    setSaveState(SaveState.Idle);
    leave();
    // A save of the typed text may still be in flight: wait for it, then compare with what is stored.
    await inflight.current;
    const persisted = (await queries.noteForDate(store.repo, date))?.data?.text ?? '';
    if (persisted === original) return;
    const result = await run(() => store.saveNote(date, original));
    if (!result.ok) {
      update({ text: original, dirty: true });
      setSaveState(SaveState.Failed);
    }
  }, [clearTimer, update, leave, store, date, run]);

  const onRegionFocus = (): void => setEditingState(true);

  const onRegionBlur = (event: FocusEvent<HTMLDivElement>): void => {
    if (swapping.current) return;
    // Moving between the editor and the buttons (Tab) stays inside editing.
    if (event.relatedTarget instanceof Node && region.current?.contains(event.relatedTarget) === true) {
      void flush();
      return;
    }
    // Switching to another window also blurs the editor: save, but stay in editing until the user is back.
    if (document.hasFocus()) setEditingState(false);
    void flush();
  };

  const onReadingClick = (event: MouseEvent<HTMLDivElement>): void => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    // Selecting text by dragging ends in a click: keep reading so the selection can be copied.
    if (window.getSelection()?.isCollapsed === false) return;
    // Links and tick boxes act on their own.
    if (target.closest('a, input, label') !== null) return;
    const source = target.closest(`[${SOURCE_LINE_ATTRIBUTE}]`)?.getAttribute(SOURCE_LINE_ATTRIBUTE);
    const line = source === null || source === undefined ? Number.NaN : Number(source);
    startEditing(Number.isFinite(line) ? line - 1 : null);
  };

  const onReadingKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'Enter' || event.target !== event.currentTarget) return;
    event.preventDefault();
    startEditing(null);
  };

  const Rendered = markdown?.default;
  const statusText =
    saveState === SaveState.Saving
      ? t('note.saving')
      : saveState === SaveState.Failed
        ? t('note.saveFailed')
        : t('note.autoSaved');

  return (
    <Card
      title={t('note.title')}
      loading={note.loading}
      error={note.error !== null}
      errorText={t('note.loadError')}
      onRetry={note.retry}
      className={styles.card}
      testId="note-card"
    >
      {showField ? (
        <div ref={region} className={styles.editRegion} onFocus={onRegionFocus} onBlur={onRegionBlur}>
          <Editor
            ref={handle}
            value={value}
            placeholder={t('note.placeholder')}
            ariaLabel={t('note.title')}
            maxLength={LIMITS.dailyNoteMax}
            onChange={change}
            onCaret={(offset) => {
              lastCaret.current = offset;
            }}
            onSave={() => void save()}
            onEscape={escape}
          />
          {editing ? (
            <div className={styles.actions} data-testid="note-actions">
              <span className={styles.status} role="status" data-testid="note-save-status">
                {statusText}
              </span>
              {/* The buttons keep the editor focused (mousedown default prevented): a press on 取消 must not first run the blur exit. */}
              <Button
                className={styles.actionButton}
                data-testid="note-cancel"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => void cancel()}
              >
                {t('note.cancel')}
              </Button>
              <Button
                className={styles.actionButton}
                variant={ButtonVariant.Primary}
                data-testid="note-save"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => void save()}
              >
                {t('note.save')}
              </Button>
            </div>
          ) : null}
        </div>
      ) : (
        <div
          className={styles.reading}
          role="group"
          tabIndex={0}
          aria-label={t('note.reading')}
          data-testid="note-reading"
          onClick={onReadingClick}
          onKeyDown={onReadingKeyDown}
        >
          {Rendered === undefined ? (
            <div className={styles.plain}>{value}</div>
          ) : (
            <Rendered text={value} onToggleTodo={toggle} />
          )}
        </div>
      )}
    </Card>
  );
}
